import classes from "./CustomLineChart.module.css";
import useTheme from "../../../../common/hooks/useTheme";
import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  Customized,
} from "recharts";
import { format } from "date-fns";
import { formatDisplayDate } from "../../../../common/utils/date";

type TooltipProps = {
  active?: boolean;
  payload?: any;
  label?: string;
  unit: string;
  id: string;
};

const CustomTooltip = ({ active, payload, label, unit, id }: TooltipProps) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    const dataPoint = payload[0].payload;
    const formattedDate = formatDisplayDate(label as string);
    const displayValue = dataPoint.label
      ? dataPoint.label
      : id !== "8_RMR" && id !== "8_AMR"
        ? `${dataPoint.value}${unit === "%" ? "" : " "}${unit}`
        : dataPoint.value === 75
        ? "High"
        : "Low";

    return (
      <div
        className={classes.tooltipContainer}
        style={{ backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN }}
      >
        <div
          className={classes.legendItemText}
          style={{ color: theme.colors_ds.NEUTRAL_TEXT_WEAK }}
        >
          {formattedDate}
        </div>
        <div
          className={classes.tooltipValue}
          style={{ color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT }}
        >
          {displayValue}
        </div>
      </div>
    );
  }

  return null;
};

// Optional background band (e.g. a biomarker's reference-range segment); a
// null bound runs to that edge of the axis.
export type ChartBand = {
  from: number | null;
  to: number | null;
  color: string;
};

// A range reading ("<1 %", "≥ 5.0", "3–4"): drawn as a hatched vertical bar
// at its date, from its bound to the axis edge (or between both bounds), with
// the line meeting it at `value` — the bound, or the midpoint of two. The
// colors are the reading's category hatch, grey when it has none.
export type ChartRange = {
  lower: number | null;
  upper: number | null;
  fill: string;
  stripe: string;
};

const RANGE_BAR_WIDTH = 7;

/**
 * The hatched bars, drawn from the chart's own scales: x from the first
 * line's computed points (so a bar sits exactly on its date) and y from the
 * Y axis. Mount through recharts' `Customized`, which supplies those props.
 *
 * Shared with the History Timeline, which passes `onHover` (its own tooltip)
 * and marks the neighbours it keeps outside its window `inWindow: false`.
 */
export const RangeBars = (props: any) => {
  const { formattedGraphicalItems, yAxisMap, offset, chartId, onHover } = props;
  const yAxis: any = yAxisMap && Object.values(yAxisMap)[0];
  const points: any[] = formattedGraphicalItems?.[0]?.props?.points ?? [];
  if (!yAxis?.scale || !offset) return null;
  const top = offset.top;
  const bottom = offset.top + offset.height;
  return (
    <g>
      {points.map((point, index) => {
        const range: ChartRange | undefined = point.payload?.range;
        if (!range || typeof point.x !== "number") return null;
        if (point.payload?.inWindow === false) return null;
        const y1 = range.upper === null ? top : yAxis.scale(range.upper);
        const y2 = range.lower === null ? bottom : yAxis.scale(range.lower);
        const yTop = Math.max(top, Math.min(y1, y2));
        const yBottom = Math.min(bottom, Math.max(y1, y2));
        // Ids go into `url(#…)`, so keep them to safe characters.
        const patternId = `range-hatch-${String(chartId).replace(
          /[^\w-]/g,
          "_"
        )}-${index}`;
        return (
          <g key={patternId}>
            <defs>
              <pattern
                id={patternId}
                width={5}
                height={5}
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(35)"
              >
                <rect width={5} height={5} fill={range.fill} />
                <line x1={0} y1={0} x2={0} y2={5} stroke={range.stripe} strokeWidth={2} />
              </pattern>
            </defs>
            <rect
              x={point.x - RANGE_BAR_WIDTH / 2}
              y={yTop}
              width={RANGE_BAR_WIDTH}
              height={Math.max(yBottom - yTop, 2)}
              fill={`url(#${patternId})`}
              onMouseEnter={onHover ? () => onHover(point.payload, yTop) : undefined}
              onMouseLeave={onHover ? () => onHover(null, 0) : undefined}
            />
          </g>
        );
      })}
    </g>
  );
};

// Round up to a "nice" axis end (1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10 × 10ⁿ), so
// small scales like 0–0.5 are not stretched to 0–5.
const niceCeil = (value: number): number => {
  if (value <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(value)));
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  const step = steps.find((s) => s * exp >= value - 1e-9) ?? 10;
  return step * exp;
};

// With bands, the axis is fitted to the bands rather than starting at 0: it
// spans all finite band limits plus the same headroom past each outer limit,
// so both open-ended end bands get a similar, modest height. Only a reading
// beyond that headroom stretches the axis further on its side. The ends are
// then snapped outward to a round tick step so the labels stay clean.
export const getBandedDomain = (
  bands: ChartBand[],
  data: { value: number }[],
  unit: string
): { domain: [number, number]; ticks: number[] } | null => {
  const limits = bands
    .flatMap((b) => [b.from, b.to])
    .filter((v): v is number => typeof v === "number" && !isNaN(v));
  if (limits.length === 0) return null;
  const values = data.map((d) => d.value);
  const lowLimit = Math.min(...limits);
  const highLimit = Math.max(...limits);
  const headroom =
    (highLimit - lowLimit) * 0.15 || Math.abs(highLimit) * 0.15 || 1;
  let top = Math.max(...values, highLimit + headroom);
  let bottom = Math.min(...values, lowLimit - headroom);
  // A scale with no negative limits or readings never dips below 0.
  if (lowLimit >= 0 && Math.min(...values) >= 0) bottom = Math.max(0, bottom);
  if (unit === "%") top = Math.min(top, 100);

  // ~5 intervals, on a 1 / 2 / 2.5 / 5 × 10ⁿ step.
  const step = niceCeil((top - bottom) / 5);
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  const round = (v: number) => Number(v.toFixed(decimals));
  const min = round(Math.floor(bottom / step + 1e-9) * step);
  const max = round(Math.ceil(top / step - 1e-9) * step);
  const ticks: number[] = [];
  for (let t = min; t <= max + step / 2; t += step) ticks.push(round(t));
  return { domain: [min, max], ticks };
};

const CustomLineChart = ({
  id,
  data,
  unit,
  bands,
}: {
  id: string;
  // `color` (optional) colors that point's dot; points without one use the
  // default line color. `range` marks a range reading ("<1"): see ChartRange.
  // `label` replaces the tooltip's value text.
  data: {
    value: number;
    dateCreated: string;
    color?: string;
    range?: ChartRange;
    label?: string;
  }[];
  top?: string;
  unit: string;
  bands?: ChartBand[];
}) => {
  const theme = useTheme();
  const isHighLow = id === "8_RMR" || id === "8_AMR";
  const revertedData = [...data]
    .reverse()
    .map((d) =>
      isHighLow
        ? { ...d, value: d.value === 100 ? 75 : d.value === 10 ? 25 : d.value }
        : d
    );

  // A range reading's bounds count toward the scale, so its bar has room.
  const scaleData = revertedData.flatMap((d) => [
    { value: d.value },
    ...[d.range?.lower, d.range?.upper]
      .filter((v): v is number => typeof v === "number")
      .map((v) => ({ value: v })),
  ]);

  // Calculate max value from data
  const maxValue =
    scaleData.length > 0 ? Math.max(...scaleData.map((d) => d.value)) : 0;
  // Determine YAxis domain based on unit and max value, rounding up to nearest 5 for non-%
  function getRoundedMax(value: number) {
    return Math.ceil(value / 5) * 5;
  }
  const defaultDomain =
    unit === "%" ? [0, 100] : [0, maxValue > 0 ? getRoundedMax(maxValue) : 4];
  const banded =
    bands && bands.length > 0
      ? getBandedDomain(bands, scaleData, unit)
      : null;
  const yAxisDomain = banded ? banded.domain : defaultDomain;

  return (
    <div
      className={classes.container}
      style={{
        borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
        backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
      }}
    >
      <div className={classes.chartArea}>
        {!isHighLow && (
          <span
            className={classes.yAxisLabel}
            style={{ color: theme.colors_ds.NEUTRAL_TEXT_WEAK }}
          >
            {unit}
          </span>
        )}
        <ResponsiveContainer width="100%" height={177}>
          <LineChart
            data={revertedData}
            margin={{
              top: 5,
              left: -25,
              bottom: -5,
            }}
          >
            <Tooltip content={<CustomTooltip unit={unit} id={id} />} />
            <XAxis
              dataKey="dateCreated"
              tickFormatter={(value) => format(new Date(value), "MMM yyyy")}
              tick={{
                fontSize: "10px",
                fontFamily: "var(--font-Medium)",
              }}
              padding={{ left: 50, right: 50 }}
              stroke={theme.colors_ds.NEUTRAL_TEXT_WEAK}
            />
            <YAxis
              domain={yAxisDomain}
              ticks={banded ? banded.ticks : undefined}
              // Show every computed tick; recharts would otherwise drop some
              // labels and leave an uneven scale.
              interval={banded ? 0 : undefined}
              tick={{
                fontSize: "10px",
                fontFamily: "var(--font-Medium)",
              }}
              tickFormatter={(value) => {
                if (id !== "8_RMR" && id !== "8_AMR") {
                  return value === 0 ? "" : value;
                } else {
                  if (value === 25) {
                    return "Low";
                  } else if (value === 75) {
                    return "High";
                  } else {
                    return "";
                  }
                }
              }}
              tickLine={id !== "8_RMR" && id !== "8_AMR"}
              stroke={theme.colors_ds.NEUTRAL_TEXT_WEAK}
            />

            {bands?.map((band, idx) => (
              <ReferenceArea
                key={idx}
                y1={band.from ?? yAxisDomain[0]}
                y2={band.to ?? yAxisDomain[1]}
                fill={band.color}
                fillOpacity={1}
                stroke="none"
                ifOverflow="hidden"
              />
            ))}

            <Customized component={<RangeBars chartId={id} />} />

            <Line
              type="monotone"
              dataKey="value"
              stroke={theme.colors_ds.PRIMARY_ICON_MAIN}
              dot={(props: any) => {
                // A range reading is its hatched bar; the line just meets it.
                if (props.payload?.range) {
                  return <g key={props.key ?? props.index} />;
                }
                const color =
                  props.payload?.color ?? theme.colors_ds.PRIMARY_ICON_MAIN;
                return (
                  <circle
                    key={props.key ?? props.index}
                    cx={props.cx}
                    cy={props.cy}
                    r={3}
                    fill={color}
                    stroke={color}
                    strokeWidth={0}
                  />
                );
              }}
              // The hovered point keeps its own color (its category, when the
              // point carries one) and just grows, so hovering never hides
              // which category the reading scored.
              activeDot={(props: any) => {
                if (props.payload?.range) {
                  return <g key={props.key ?? props.index} />;
                }
                const color =
                  props.payload?.color ?? theme.colors_ds.PRIMARY_ICON_MAIN;
                return (
                  <circle
                    key={props.key ?? props.index}
                    cx={props.cx}
                    cy={props.cy}
                    r={6}
                    fill={color}
                    stroke={color}
                    strokeWidth={1}
                  />
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CustomLineChart;
